// A sandboxed code execution in the chat: the program the analyst
// wrote plus its terminal output, Cursor-style.
//
// Always renders on the dark editor surface (same as MarkdownBody's
// CodeCard) in both themes: traffic-light header with status + runtime,
// Prism-highlighted Python, then an "Output" terminal strip (stdout in
// ink, stderr/tracebacks in the danger tone). Long code collapses past
// ~16 lines with a fade + expander so a big backfill script doesn't
// swallow the thread.

import React, { useMemo, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';

SyntaxHighlighter.registerLanguage('python', python);

const SURFACE = '#111411';
const HEADER = '#171a17';
const BORDER = '#262b26';
const INK = '#e6ebe4';
const MUTED = '#96a096';
const FAINT = '#6b756b';
const LIME = '#85d818';
const DANGER = '#ff8a80';

const COLLAPSED_LINES = 16;

const fmtDuration = (ms) => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n < 1000 ? `${Math.round(n)}ms` : `${(n / 1000).toFixed(1)}s`;
};

const CodeRunBlock = ({ spec }) => {
  const {
    code = '', stdout = '', stderr = '', error = null,
    ok = false, duration_ms: durationMs, ops_used: opsUsed,
    timed_out: timedOut, truncated,
  } = spec || {};

  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const lines = useMemo(() => (code ? code.split('\n').length : 0), [code]);
  const collapsible = lines > COLLAPSED_LINES + 4;
  const shownCode = useMemo(() => {
    if (!collapsible || expanded) return code;
    return code.split('\n').slice(0, COLLAPSED_LINES).join('\n');
  }, [code, collapsible, expanded]);

  // The child already merges the traceback into stderr in most paths;
  // when it only arrives as `error`, surface it in the output strip.
  const errText = [stderr, !ok && error && !stderr.includes(error) ? error : '']
    .filter(Boolean).join('\n').trim();
  const outText = (stdout || '').replace(/\n$/, '');
  const hasOutput = Boolean(outText || errText);

  const handleCopy = () => {
    if (!code) return;
    try {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_e) { /* ignore */ }
  };

  const statusText = ok ? 'ran' : (timedOut ? 'timed out' : 'failed');
  const meta = [
    fmtDuration(durationMs),
    Number(opsUsed) > 0 ? `${opsUsed} data call${opsUsed === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <Box
      sx={{
        my: 1.25,
        borderRadius: '14px',
        overflow: 'hidden',
        border: `1px solid ${BORDER}`,
        maxWidth: 760,
      }}
    >
      {/* Header: traffic lights · python · status · runtime · copy */}
      <Stack
        direction="row" alignItems="center" spacing={1}
        sx={{ px: 1.5, py: 0.7, bgcolor: HEADER, borderBottom: `1px solid ${BORDER}` }}
      >
        <Stack direction="row" spacing={0.55}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6057' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#febc2e' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#28c840' }} />
        </Stack>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 10.5, color: MUTED }}>
          python
        </Typography>
        <Box
          sx={{
            width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
            bgcolor: ok ? LIME : DANGER,
          }}
        />
        <Typography sx={{ fontSize: 10.5, color: ok ? MUTED : DANGER, fontWeight: 600 }}>
          {statusText}
        </Typography>
        {meta && (
          <Typography sx={{ fontSize: 10.5, color: FAINT }}>{meta}</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={copied ? 'Copied' : 'Copy code'} arrow>
          <IconButton
            size="small" onClick={handleCopy}
            sx={{ p: 0.4, color: copied ? LIME : FAINT, '&:hover': { color: INK } }}
          >
            {copied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* The program */}
      <Box sx={{ position: 'relative', bgcolor: SURFACE }}>
        <SyntaxHighlighter
          language="python"
          style={oneDark}
          customStyle={{
            margin: 0,
            padding: '14px 16px',
            background: 'transparent',
            fontSize: 12,
            lineHeight: 1.65,
          }}
          codeTagProps={{ style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' } }}
        >
          {shownCode || ' '}
        </SyntaxHighlighter>
        {collapsible && !expanded && (
          <Box
            sx={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: 56,
              background: `linear-gradient(180deg, rgba(17,20,17,0), ${SURFACE})`,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>
      {collapsible && (
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            px: 1.5, py: 0.55,
            bgcolor: SURFACE,
            borderTop: `1px solid ${BORDER}`,
            display: 'flex', alignItems: 'center', gap: 0.5,
            cursor: 'pointer',
            color: FAINT,
            '&:hover': { color: INK },
          }}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: 15,
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 140ms',
            }}
          />
          <Typography sx={{ fontSize: 11 }}>
            {expanded ? 'Collapse' : `Show all ${lines} lines`}
          </Typography>
        </Box>
      )}

      {/* Output terminal strip */}
      {hasOutput && (
        <Box sx={{ bgcolor: SURFACE, borderTop: `1px solid ${BORDER}` }}>
          <Typography
            sx={{
              px: 1.5, pt: 1, pb: 0.25,
              fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em',
              textTransform: 'uppercase', color: FAINT,
            }}
          >
            Output{truncated ? ' · truncated' : ''}
          </Typography>
          <Box
            component="pre"
            sx={{
              m: 0, px: 1.75, pb: 1.5, pt: 0.5,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 11.5, lineHeight: 1.6,
              color: INK,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 260,
              overflowY: 'auto',
            }}
          >
            {outText}
            {outText && errText ? '\n' : ''}
            {errText && (
              <Box component="span" sx={{ color: DANGER }}>{errText}</Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CodeRunBlock;
